export const useReviewApi = () => {
  const config = useRuntimeConfig();
  const auth = useAuthStore();
  const apiBase = config.public.apiBase;

  const getHeaders = (isMultipart = false) => {
    const headers = {
      Accept: 'application/json',
    };
    if (auth.token) {
      headers.Authorization = `Bearer ${auth.token}`;
    }
    if (!isMultipart) {
      headers['Content-Type'] = 'application/json';
    }
    return headers;
  };

  /**
   * Fetch paginated food reviews feed with filtering and sorting
   */
  const fetchReviews = async (params = {}) => {
    const query = {};

    if (params.shop_id) query['filter[shop_id]'] = params.shop_id;
    if (params.user_id) query['filter[user_id]'] = params.user_id;
    if (params.tag && params.tag !== 'All') query['filter[tag]'] = params.tag;
    if (params.search) query['filter[restaurant_name]'] = params.search;
    if (params.sort) query.sort = params.sort;
    if (params.page) query.page = params.page;
    if (params.limit) query.limit = params.limit;

    return await $fetch(`${apiBase}/user/reviews`, {
      method: 'GET',
      headers: getHeaders(),
      query,
    });
  };

  /**
   * Fetch a single review by ID
   */
  const fetchReview = async (id) => {
    return await $fetch(`${apiBase}/user/reviews/${id}`, {
      method: 'GET',
      headers: getHeaders(),
    });
  };

  /**
   * Create a new food review post (multipart/form-data)
   */
  const createReview = async (formData) => {
    return await $fetch(`${apiBase}/user/reviews`, {
      method: 'POST',
      headers: getHeaders(true),
      body: formData,
    });
  };

  /**
   * Add, change, or remove reaction on a review
   * @param {number|string} reviewId
   * @param {string} type ('like', 'love', 'care', 'haha', 'wow', 'sad', 'angry', 'none')
   */
  const reactToReview = async (reviewId, type) => {
    return await $fetch(`${apiBase}/user/reviews/${reviewId}/react`, {
      method: 'POST',
      headers: getHeaders(),
      body: { type },
    });
  };

  /**
   * Delete a review post
   */
  const deleteReview = async (reviewId) => {
    return await $fetch(`${apiBase}/user/reviews/${reviewId}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
  };

  /**
   * Fetch comments for a specific review
   */
  const fetchComments = async (reviewId, page = 1, limit = 15) => {
    return await $fetch(`${apiBase}/user/reviews/${reviewId}/comments`, {
      method: 'GET',
      headers: getHeaders(),
      query: { page, limit },
    });
  };

  /**
   * Add a text-only comment to a review
   */
  const addComment = async (reviewId, comment) => {
    return await $fetch(`${apiBase}/user/reviews/${reviewId}/comments`, {
      method: 'POST',
      headers: getHeaders(),
      body: { comment },
    });
  };

  /**
   * Delete a comment
   */
  const deleteComment = async (reviewId, commentId) => {
    return await $fetch(`${apiBase}/user/reviews/${reviewId}/comments/${commentId}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
  };

  /**
   * Fetch available restaurant list for dropdown tagging
   */
  const fetchAvailableShops = async () => {
    try {
      const response = await $fetch(`${apiBase}/shop`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      return response.data || [];
    } catch {
      return [];
    }
  };

  return {
    fetchReviews,
    fetchReview,
    createReview,
    reactToReview,
    deleteReview,
    fetchComments,
    addComment,
    deleteComment,
    fetchAvailableShops,
  };
};
